import { getTierThreshold, getTiersAbove } from 'loyalty/selectors/getLoyaltyTierThresholds'

export interface TierRemaining {
  creditsRemaining: number
  nightsRemaining: number
  targetCredits: number
  targetNights: number
}

/**
 * Calculates how many status credits and nights remain to reach
 * (or retain) a target tier.
 *
 * @param mode - 'upgrade' for progressing to the next tier,
 *               'retain' for keeping the current tier.
 * @param tierThresholds - When set, zero values from the account API fall back to loyalty-config
 *   thresholds for the current (retain) or next (upgrade) tier — same as `useLoyaltyTierProgressionMode`.
 *   Without this, Bronze can show 93/0 when the API nullifies retention zeros.
 */
export default function calculateTierRemaining(
  account: App.LoyaltyAccount,
  mode: 'upgrade' | 'retain' = 'upgrade',
  tierThresholds?: Record<App.LoyaltyTier, App.LoyaltyTierThreshold>,
): TierRemaining {
  const statusCredits = account.statusCredits ?? 0
  const numberOfNights = account.numberOfNights ?? 0

  const currentTierThreshold = tierThresholds ? getTierThreshold(tierThresholds, account.tier) : undefined
  const [nextTier] = getTiersAbove(account.tier)
  const nextTierThreshold = tierThresholds && nextTier ? getTierThreshold(tierThresholds, nextTier) : undefined

  let creditTarget: number
  let nightTarget: number
  if (mode === 'retain') {
    creditTarget = (account.totalCreditsForTierRetention ?? 0) || currentTierThreshold?.statusCredits || 0
    nightTarget = (account.numberOfNightsForTierRetention ?? 0) || currentTierThreshold?.nights || 0
  } else {
    creditTarget = (account.totalCreditsForTierUpgrade ?? 0) || nextTierThreshold?.statusCredits || 0
    nightTarget = (account.numberOfNightsForTierUpgrade ?? 0) || nextTierThreshold?.nights || 0
  }

  return {
    creditsRemaining: Math.max(0, creditTarget - statusCredits),
    nightsRemaining: Math.max(0, nightTarget - numberOfNights),
    targetCredits: creditTarget,
    targetNights: nightTarget,
  }
}
